/**
 * CategoryTree Component
 * 
 * Hierarchical tree display with expand/collapse functionality
 * Following the design specifications
 * 
 * Features:
 * - Recursive tree rendering
 * - Expand/collapse functionality
 * - ARIA tree roles for accessibility
 * - Keyboard navigation support
 */

import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, MoreVertical, Edit, Trash2 } from 'lucide-react';
import { Category, CategoryType } from '../../../../types/category.types';
import { getCategoryIconComponent } from '../../../../utils/icon-utils';
import { isAdmin } from '../../../../utils/admin-utils';

interface CategoryTreeProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
}

interface CategoryNodeProps {
  category: Category;
  allCategories: Category[];
  level: number;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  selectedIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
}

function CategoryNode({
  category,
  allCategories,
  level,
  onEdit,
  onDelete,
  selectedIds,
  onSelectionChange,
  expandedIds,
  onToggleExpand
}: CategoryNodeProps): React.ReactElement {
  const children = allCategories.filter(cat => cat.parentId === category.id);
  const isExpanded = expandedIds.has(category.id);
  const hasChildren = children.length > 0;
  const isSelected = selectedIds?.has(category.id) || false;
  const [showMenu, setShowMenu] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  // Check if user is admin
  useEffect(() => {
    isAdmin()
      .then((adminStatus) => {
        setIsUserAdmin(adminStatus);
        setIsCheckingAdmin(false);
      })
      .catch(() => {
        setIsUserAdmin(false);
        setIsCheckingAdmin(false);
      });
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const handleToggleExpand = () => {
    if (hasChildren) {
      onToggleExpand(category.id);
    }
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange || category.isSystem) return; // System categories can't be selected
    
    const newSelected = new Set(selectedIds);
    if (e.target.checked) {
      newSelected.add(category.id);
    } else {
      newSelected.delete(category.id);
    }
    onSelectionChange(newSelected);
  };

const IconComponent = getCategoryIconComponent(category);

const getAccentColor = (): string => {
  if (category.color) {
    return category.color;
  }
  if (category.type === CategoryType.INCOME) {
    return '#22c55e'; // green-500
  }
  if (category.type === CategoryType.TRANSFER) {
    return '#0ea5e9'; // sky-500
  }
  return '#ef4444'; // red-500 for expenses
};

const accentColor = getAccentColor();

const iconBackgroundColor = (() => {
  const hex = accentColor.replace('#', '');
  if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }
  }
  return 'rgba(99, 102, 241, 0.12)'; // fallback indigo
})();

  // Get type badge color
  const getTypeBadgeColor = () => {
    if (category.type === CategoryType.INCOME) {
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
    if (category.type === CategoryType.TRANSFER) {
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
    return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  };

  return (
    <div className="category-node" role="treeitem" aria-level={level + 1}>
      <div 
        className={`flex items-center gap-3 py-3 px-4 border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${
          isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
        }`}
        style={{ paddingLeft: `${level * 20 + 16}px` }}
      >
        {/* Expand/Collapse Button */}
        <button
          onClick={handleToggleExpand}
          className={`flex-shrink-0 w-5 h-5 flex items-center justify-center transition-transform ${
            hasChildren ? 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 rounded' : 'cursor-default opacity-0'
          }`}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={hasChildren ? isExpanded : undefined}
        >
          {hasChildren && (
            isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" aria-hidden="true" />
            )
          )}
        </button>

        {/* Selection Checkbox */}
        {onSelectionChange && !category.isSystem && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleSelect}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            aria-label={`Select ${category.name}`}
          />
        )}

        {/* Category Icon */}
        <div
          className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-lg"
          style={{ backgroundColor: iconBackgroundColor }}
        >
          {IconComponent && (
            <IconComponent className="w-5 h-5" style={{ color: accentColor }} aria-hidden="true" />
          )}
          {!IconComponent && (
            <span className="text-sm font-medium" style={{ color: accentColor }}>
              {category.name.charAt(0)}
            </span>
          )}
        </div>

        {/* Category Info */}
        <div className="flex-1 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {category.name}
              </span>
              {hasChildren && (
                <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {children.length} {children.length === 1 ? 'subcategory' : 'subcategories'}
                </span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getTypeBadgeColor()}`}>
                {category.type === CategoryType.INCOME ? 'Income' : category.type === CategoryType.TRANSFER ? 'Transfer' : 'Expense'}
              </span>
              {category.isSystem && (
                <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                  System
                </span>
              )}
              {category.excludeFromSpending && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">
                  Excluded from insights
                </span>
              )}
            </div>
            {category.note && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                {category.note}
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        {hasChildren ? (
          // Three-dot menu for parent categories
          <div className="relative flex-shrink-0" ref={menuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if ((!category.isSystem || isUserAdmin) && !isCheckingAdmin) {
                  setShowMenu(!showMenu);
                }
              }}
              disabled={isCheckingAdmin || (category.isSystem && !isUserAdmin)}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={`Actions for ${category.name}`}
              aria-expanded={showMenu}
            >
              <MoreVertical className="w-4 h-4" aria-hidden="true" />
            </button>
            
            {showMenu && (!category.isSystem || isUserAdmin) && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                <div className="py-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onEdit(category);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" aria-hidden="true" />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                      onDelete(category);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (!isCheckingAdmin && (!category.isSystem || isUserAdmin)) ? (
          // Direct Edit/Delete icons for categories without children (only if not system, or if admin)
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(category);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label={`Edit ${category.name}`}
            >
              <Edit className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(category);
              }}
              className="p-1.5 text-red-500 hover:text-red-700 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              aria-label={`Delete ${category.name}`}
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div role="group" aria-label={`Children of ${category.name}`}>
          {children.map((child) => (
            <CategoryNode
              key={child.id}
              category={child}
              allCategories={allCategories}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              selectedIds={selectedIds}
              onSelectionChange={onSelectionChange}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CategoryTree({
  categories,
  onEdit,
  onDelete,
  selectedIds = new Set(),
  onSelectionChange
}: CategoryTreeProps): React.ReactElement {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Build tree structure
  const buildTree = (): Category[] => {
    const categoryMap = new Map<string, Category>();
    const rootCategories: Category[] = [];

    // First pass: create map
    categories.forEach(cat => categoryMap.set(cat.id, cat));

    // Second pass: build tree
    categories.forEach(cat => {
      if (!cat.parentId) {
        rootCategories.push(cat);
      }
    });

    return rootCategories;
  };

  const handleToggleExpand = (id: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const rootCategories = buildTree();

  if (rootCategories.length === 0) {
    return (
      <div className="p-12 text-center border-b border-gray-100 dark:border-gray-700/50">
        <p className="text-gray-500 dark:text-gray-400">No categories found. Create your first category to get started.</p>
      </div>
    );
  }

  return (
    <div role="tree" aria-label="Categories tree">
      {rootCategories.map((category) => (
        <CategoryNode
          key={category.id}
          category={category}
          allCategories={categories}
          level={0}
          onEdit={onEdit}
          onDelete={onDelete}
          selectedIds={selectedIds}
          onSelectionChange={onSelectionChange}
          expandedIds={expandedIds}
          onToggleExpand={handleToggleExpand}
        />
      ))}
    </div>
  );
}

